/** @prettier */

/**
 * 仮パスワード変更
 */

import {useState, useEffect} from 'react';
import {Stack, Box, FormControlLabel, Checkbox} from '@mui/material';
import {Auth} from 'aws-amplify';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';

//MTH component import
import {ChkInputTextbox} from '../../component/ChkInputTextbox';
import {PrimaryButton} from '../../component/BasicButton';
//Style import
import '../../css/common/common.css';

import {apiErrorAlert} from '../../functions/apiErrorAlert';
import {routeList} from '../PrivateRoute';
import {file, format} from '../../define';
import {getPublicS3Files, languageInfo} from '../help/helpFunction';

type ArgumentType = {
  signInData: any;
  emailAddr: string;
};
const ChangeTempPassword = (props: ArgumentType) => {
  const navigate = useNavigate();
  const trans = useTranslation().t; //翻訳関数

  const [newPwd, setNewPwd] = useState(''); //パスワード
  const [confirmNewPwd, setConfirmNewPwd] = useState(''); //パスワード（2回目の入力）
  const [changePwdBtnStatus, setChangePwdBtnStatus] = useState(true); //Change Passwordボタン有効フラグ
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ

  //TextBoxのエラー状態
  const [newPwdError, setNewPwdError] = useState(false); //パスワード
  const [confirmNewPwdError, setConfirmNewPwdError] = useState(false); //パスワード（2回目の入力）

  //フォーマット正常フラグ
  const [pwdValid, setPwdValid] = useState(true); //パスワード
  const [pwdMatch, setPwdMatch] = useState(true); //パスワード（2回目の入力）

  const [checkBox, setCheckBox] = useState(false); //チェックボックス

  //Change Passwordボタン有効無効判定
  useEffect(() => {
    if (
      newPwd === '' ||
      confirmNewPwd === '' ||
      newPwdError ||
      confirmNewPwdError ||
      apiWait ||
      checkBox === false
    ) {
      setChangePwdBtnStatus(true);
    } else {
      setChangePwdBtnStatus(false);
    }
  }, [
    newPwd,
    confirmNewPwd,
    newPwdError,
    confirmNewPwdError,
    apiWait,
    checkBox,
  ]);

  /**
   * 現在のUTCの日付をyyyymmdd形式の文字列で返す関数
   * @returns {string} yyyymmdd形式のUTC日付
   */
  const formatTodayDateUtc = () => {
    const now = new Date();
    const day = String(now.getUTCDate()).padStart(2, '0');
    const month = String(now.getUTCMonth() + 1).padStart(2, '0');
    const year = now.getUTCFullYear();

    return `${year}${month}${day}`;
  };

  /**
   * 正規表現パターンを生成する関数
   *プライバシーポリシーまたはユーザー規約に一致し、`20`で始まる8桁の日付（ddmmyyyy形式）と
   * 言語コードが含まれたPDFファイル名にマッチする正規表現パターンを生成する。
   * @returns {RegExp} 指定されたパターンにマッチする正規表現オブジェクト
   */
  const getRegexPattern = () => {
    const languagePattern = languageInfo.join('|');
    return new RegExp(
      `^(privacy-policy|user-terms)_(20\\d{6})_(${languagePattern})\\.pdf$`
    );
  };

  /**
   * Change Passwordボタン押下時処理
   * @returns
   */
  const handleSubmit = () => {
    setAPIWait(true);
    setPwdMatch(true);
    setPwdValid(true);

    //パスワード一致チェック
    if (newPwd !== confirmNewPwd) {
      setPwdMatch(false);
      setAPIWait(false);
      return;
    }
    //パスワード形式チェック
    if (
      newPwd.search(/[A-Z]/) === -1 || //大文字アルファベットなし
      newPwd.search(/[a-z]/) === -1 || //小文字アルファベットなし
      newPwd.search(/[0-9]/) === -1 || //数字なし
      newPwd.search(/[*:;@?%#&=$[\](),.!_/'+\-^{}"\\><|~`]/) === -1 || //特殊文字なし
      newPwd.match(/[^,.!_/'()[\]+=\-$#&@?%*:;^{}"\\><|~`A-Za-z0-9 ]/) || //使用可能文字以外使用
      newPwd.search(props.emailAddr.slice(0, props.emailAddr.indexOf('@'))) !==
        -1 || //パスワードにユーザIDが含まれる
      newPwd.length < format.PASSWORD_LENGTH_MIN //文字数 10文字未満
    ) {
      setPwdValid(false);
      setAPIWait(false);
      return;
    }

    //仮パスワードを変更する
    Auth.completeNewPassword(
      props.signInData, //signInの返り値のuserData
      newPwd //変更する新しいパスワード
    )
      .then(async data => {
        //招待ユーザーに対する通知は2ndStepリリースを優先してアラート通知のみとする。

        //S3の公開バケットからファイル情報一覧を取得
        try {
          const fileList = await getPublicS3Files(
            file.PUBLIC_DOCUMENT_S3_KEY_PREFIX
          );

          //ファイル情報一覧からファイル名だけ取得
          const filesNameList = fileList.flatMap(file => {
            return file.key ? [file.key] : [];
          });

          const regex = getRegexPattern();

          const currentDateString = formatTodayDateUtc();

          // 適用日が未来のファイルが存在するか確認
          const matchedFiles = filesNameList.filter(fileName => {
            const match = fileName.match(regex);
            if (match === null) {
              return false;
            }

            const fileDateString = match[2];
            return fileDateString > currentDateString;
          });

          if (matchedFiles.length > 0) {
            alert(
              'M-ACCESS User Terms and/or Privacy Policy are scheduled to be updated.Please review the new documents from the “HELP” tab.'
            );
          }
        } catch (error) {
          //ユーザーへの通知は不要という方針なので何もしない
        }

        navigate(routeList.mySite.path, {replace: true});
        window.location.reload();
      })
      .catch(err => {
        apiErrorAlert(trans);
      })
      .finally(() => {
        setAPIWait(false);
      });
  };

  /**
   * チェックボックス状態判定
   * @param event
   */
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCheckBox(event.target.checked);
  };

  return (
    <Stack
      spacing={2}
      className="changeTempPassword__leftFrame__input__contents">
      <h1>Change temp password</h1>
      {/* 新しいパスワードと確認用パスワードが不一致の場合はメッセージを表示 */}
      {pwdMatch === false ? (
        <Box className="changeTempPassword__leftFrame__input__contents__errorText">
          Passwords do not match. Please try again.
        </Box>
      ) : (
        <></>
      )}
      {/* 新しいパスワードが許容条件を満たしていない場合はメッセージを表示 */}
      {pwdValid === false ? (
        <Box className="changeTempPassword__leftFrame__input__contents__errorText">
          The password is in an invalid format.
        </Box>
      ) : (
        <></>
      )}
      {/* 新しいパスワード */}
      <ChkInputTextbox
        setValue={setNewPwd}
        setError={setNewPwdError}
        chkInputTxtOK={format.PASSWORD_PATTERN_OK}
        chkLength={format.PASSWORD_LENGTH_MAX}
        type="password"
        label="New password"
        value={newPwd}
        helperText={`The password must be at least ${format.PASSWORD_LENGTH_MIN} characters long including the following; At least one lowercase and uppercase character, a number and a symbol.It must not include any details of your email address.`}
        errHelperText="Incorrect entry."
        variant="standard"
        autoFocus={true}
      />
      {/* 確認用新しいパスワード */}
      <ChkInputTextbox
        setValue={setConfirmNewPwd}
        setError={setConfirmNewPwdError}
        chkInputTxtOK={format.PASSWORD_PATTERN_OK}
        chkLength={format.PASSWORD_LENGTH_MAX}
        type="password"
        label="Confirm new password"
        value={confirmNewPwd}
        helperText="Re-enter the password you entered above."
        errHelperText="Incorrect entry."
        variant="standard"
      />
      {/** 規約への同意のお願い */}
      <Box>
        In order to proceed with creating an M-ACCESS account and using the
        M-ACCESS Service, please confirm that you have read the M-ACCESS Service
        Privacy Policy and User terms, agree to the basis upon which we will
        collect and process your Personal Data and Technical Data.
      </Box>
      {/** 規約 */}
      <Box className="changeTempPassword__leftFrame__input__contents__registerAccountServiceInfo__contents">
        <Box
          component="span"
          className="changeTempPassword__leftFrame__input__contents__registerAccountServiceInfo__contents--underlineFont">
          Acknowledgement of the M-ACCESS Privacy Policy
          <br />
        </Box>
        <Box component="p">
          I acknowledge that I have read and understood the information in&nbsp;
          <Link
            to={routeList.privacyPolicy.path}
            id="mv-PrivacyPolicyOfService"
            target="_blank">
            the M-ACCESS Privacy Policy
          </Link>
          &nbsp;regarding the collection and use of my Personal Data.
        </Box>
        <Box
          component="span"
          className="changeTempPassword__leftFrame__input__contents__registerAccountServiceInfo__contents--underlineFont">
          Consent to the collection and use of Technical Data under e-Privacy
          Laws
          <br />
        </Box>
        <Box component="p">
          To ensure that MTH's collection and use of Technical Data is in
          compliance with EEA and UK e-Privacy laws, I hereby consent, on behalf
          of myself or the organisation which I represent, to Technical Data
          being collected by MTH from the Unit(s) installed on the Site(s) for
          which I am, or my organisation is, responsible and used by MTH to
          provide the M-ACCESS Service and related purposes. This consent is
          given freely. I am aware that I or my organisation may revoke this
          consent at any given time by deleting the M-ACCESS Service account. I
          understand that if consent is revoked by deleting the account then I
          or my organisation shall no longer be able to use the M-ACCESS Service
          and that any collection and processing of Technical Data which took
          place before consent was revoked will remain lawful.
        </Box>
        <Box
          component="span"
          className="changeTempPassword__leftFrame__input__contents__registerAccountServiceInfo__contents--underlineFont">
          Acknowledgement of the M-ACCESS User terms
          <br />
        </Box>
        <Box component="p">
          I acknowledge that I have read and understood the information in&nbsp;
          <Link
            to={routeList.registerAccountTermsOfService.path}
            id="mv-UsersTermsOfService"
            target="_blank">
            the M-ACCESS User terms.
          </Link>
        </Box>
      </Box>
      {/** チェックボックス */}
      <FormControlLabel
        control={<Checkbox onChange={handleChange} id={'mv-checkboxAccept'} />}
        checked={checkBox}
        label="I agree with the above 3 items"
      />
      {/* パスワード変更ボタン */}
      <div className="changeTempPassword__leftFrame__input__contents__button">
        <PrimaryButton
          onClick={handleSubmit}
          buttonText={'Change Password'}
          disabled={changePwdBtnStatus}
          className="changeTempPassword__leftFrame__input__contents__button--changeTempPassword"
          apiWait={apiWait}
        />
      </div>
    </Stack>
  );
};
export default ChangeTempPassword;
